import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Paper, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getCatalogueByCode } from "../../api/common/catalogues.ts";
import { deleteStockMove, getStockMoveList } from "../../api/stockmoves/index.ts";
import { getPlantList } from "../../api/enterprise";
import { getWarehouseList } from "../../api/warehouses/index.ts";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import syntheticStockMoveService from "../../services/syntheticStockMoveService";
import LGModalCrud from "./LGModalCrud";
import SyntheticInboundStockMoveForm from "./SyntheticInboundStockMoveForm";

const PAGE_SIZE = 500;

const pickFirstCatalogueEntry = (res) => {
  const data = res?.data;
  return Array.isArray(data) ? data[0] : data;
};

const escapeCsvCell = (value) => {
  if (value == null || value === "") return '""';
  const stringValue = String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const mapApiRowToGridRow = (sm) => {
  const qty = Number(sm.quantity) || 0;
  const unitPrice = sm.unitCost != null ? Number(sm.unitCost) : 0;
  const receiptAcknowledgements = sm.receiptAcknowledgements ?? [];
  const purchaseOrder =
    sm.sourceId != null
      ? {
          id: sm.sourceId,
          code: sm.purchaseOrderCode ?? "",
          currencyId: sm.currencyId,
          currency: sm.currency,
        }
      : null;

  return {
    id: sm.id,
    purchaseOrder,
    receiptAcknowledgements,
    moveDate: sm.moveDate,
    product: sm.product ? { ...sm.product } : null,
    warehouseId: sm.warehouseId,
    quantity: qty,
    unitPrice,
    value: qty * unitPrice,
    hasSyntheticOutbound: Boolean(sm.hasSyntheticOutbound),
  };
};

export default function SyntheticInboundStockMoveCrud() {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const [moveTypeId, setMoveTypeId] = useState(null);
  const [entityPOTypeId, setEntityPOTypeId] = useState(null);
  const [startDate, setStartDate] = useState(() => dayjs().subtract(1, "week"));
  const [endDate, setEndDate] = useState(() => dayjs());
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [purchaseOrderCodeInput, setPurchaseOrderCodeInput] = useState("");
  const [purchaseOrderCodeFilter, setPurchaseOrderCodeFilter] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setPurchaseOrderCodeFilter(purchaseOrderCodeInput.trim()), 400);
    return () => clearTimeout(id);
  }, [purchaseOrderCodeInput]);

  useEffect(() => {
    const loadCatalogues = async () => {
      try {
        const [moveTypeResponse, poTypeResponse] = await Promise.all([
          getCatalogueByCode("MOVETYPE_SYNTHETIC_INBOUND"),
          getCatalogueByCode("ENTITY_PURCHASE_ORDER"),
        ]);
        setMoveTypeId(pickFirstCatalogueEntry(moveTypeResponse)?.id ?? null);
        setEntityPOTypeId(pickFirstCatalogueEntry(poTypeResponse)?.id ?? null);
      } catch (error) {
        console.error(error);
      }
    };

    loadCatalogues();
  }, []);

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const [plantsResponse, warehousesResponse] = await Promise.all([
          selectedCompany?.id
            ? getPlantList({
                companyId: selectedCompany.id,
                pageSize: 500,
                pageNumber: 1,
              })
            : Promise.resolve({ data: [] }),
          getWarehouseList(),
        ]);

        const plants = Array.isArray(plantsResponse.data) ? plantsResponse.data : [];
        const plantIds = new Set(plants.map((plant) => plant.id));
        const allWarehouses = Array.isArray(warehousesResponse.data)
          ? warehousesResponse.data
          : [];
        const availableWarehouses = selectedCompany?.id
          ? allWarehouses.filter((warehouse) => plantIds.has(warehouse.plantId))
          : allWarehouses;

        setWarehouseOptions(availableWarehouses);
      } catch (error) {
        console.error(error);
      }
    };

    loadWarehouses();
  }, [selectedCompany?.id]);

  const fetchAllPages = useCallback(
    async (params = {}) => {
      if (!selectedCompany?.id || !moveTypeId) return [];

      const poCode =
        typeof params.purchaseOrderCode === "string" && params.purchaseOrderCode.trim()
          ? params.purchaseOrderCode.trim()
          : undefined;

      const rows = [];
      let pageNumber = 1;

      while (true) {
        const result = await getStockMoveList({
          companyId: selectedCompany.id,
          moveTypeId,
          pageSize: PAGE_SIZE,
          pageNumber,
          startDate: startDate?.format("YYYY-MM-DD"),
          endDate: endDate?.format("YYYY-MM-DD"),
          ...(poCode ? { purchaseOrderCode: poCode } : {}),
        });
        const batch = Array.isArray(result) ? result : [];
        rows.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        pageNumber += 1;
      }

      return rows;
    },
    [selectedCompany?.id, moveTypeId, startDate, endDate]
  );

  const handleExportCsv = async () => {
    let rows = [];
    try {
      rows = (await fetchAllPages({ purchaseOrderCode: purchaseOrderCodeFilter })).map(
        mapApiRowToGridRow
      );
    } catch (error) {
      openSnack(handleError(error), "error");
      return;
    }

    if (rows.length === 0) {
      openSnack("No hay datos para exportar", "warning");
      return;
    }

    const headers = [
      "Código OC",
      "Acuses (código)",
      "Acuses (factura)",
      "Fecha movimiento",
      "Código producto",
      "Cantidad",
      "Precio unitario",
      "Valor",
    ];

    const csvRows = rows.map((row) => {
      const ras = row.receiptAcknowledgements || [];
      const codes = ras.map((receipt) => receipt.code).join("|");
      const invoices = ras.map((receipt) => receipt.invoiceNo).join("|");
      const moveDateText = row.moveDate
        ? dayjs(row.moveDate).format("DD/MM/YYYY HH:mm")
        : "";

      return [
        escapeCsvCell(row.purchaseOrder?.code ?? ""),
        escapeCsvCell(codes),
        escapeCsvCell(invoices),
        escapeCsvCell(moveDateText),
        escapeCsvCell(row.product?.code ?? ""),
        escapeCsvCell(row.quantity),
        escapeCsvCell(row.unitPrice),
        escapeCsvCell(row.value),
      ].join(",");
    });

    const csvContent = "\uFEFF" + [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `entradas_sinteticas_${dayjs().format("YYYYMMDD_HHmmss")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    openSnack("CSV exportado", "success");
  };

  const handleGenerateOutbounds = async () => {
    if (selectedRows.length === 0) {
      openSnack("Seleccione al menos una fila", "warning");
      return;
    }

    try {
      await syntheticStockMoveService.createSyntheticOutbounds({
        StockMoveIds: selectedRows,
        CompanyId: selectedCompany.id,
      });
      openSnack("Salidas contables creadas exitosamente", "success");
      setSelectedRows([]);
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "purchaseOrderCode",
        headerName: "Orden de compra",
        flex: 0.12,
        minWidth: 110,
        valueGetter: (_, row) => row.purchaseOrder?.code || "-",
      },
      {
        field: "warehouseName",
        headerName: "Almacén",
        flex: 0.11,
        minWidth: 110,
        valueGetter: (_, row) =>
          warehouseOptions.find((warehouse) => warehouse.id === row.warehouseId)?.name ||
          String(row.warehouseId ?? ""),
      },
      {
        field: "raCodes",
        headerName: "Contrarrecibos",
        flex: 0.18,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const ras = params.row.receiptAcknowledgements || [];
          return (
            <Stack direction="column" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
              {ras.map((receipt, index) => (
                <Chip key={`${receipt.code}-${index}`} size="small" label={receipt.code} />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "raInvoices",
        headerName: "Facturas",
        flex: 0.18,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const ras = params.row.receiptAcknowledgements || [];
          return (
            <Stack direction="column" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
              {ras.map((receipt, index) => (
                <Chip
                  key={`${receipt.invoiceNo}-${index}`}
                  size="small"
                  label={receipt.invoiceNo || "—"}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "moveDate",
        headerName: "Fecha movimiento",
        flex: 0.14,
        minWidth: 150,
        valueFormatter: (value) =>
          value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "",
      },
      {
        field: "productCode",
        headerName: "Producto",
        flex: 0.1,
        minWidth: 100,
        valueGetter: (_, row) => row.product?.code || "-",
      },
      {
        field: "quantity",
        headerName: "Cantidad",
        type: "number",
        flex: 0.08,
        minWidth: 90,
      },
      {
        field: "unitPrice",
        headerName: "Precio unitario",
        type: "number",
        flex: 0.1,
        minWidth: 110,
      },
      {
        field: "value",
        headerName: "Valor",
        type: "number",
        flex: 0.1,
        minWidth: 100,
      },
    ],
    [warehouseOptions]
  );

  const crudProps = useMemo(
    () => ({
      actions: ["create", "update", "delete", "select"],
      serverDrivenPagination: false,
      fetchFunction: async (queryParams) => ({
        data: (await fetchAllPages(queryParams)).map(mapApiRowToGridRow),
      }),
      deleteFunction: (id) => deleteStockMove(id),
      data: {
        companyId: selectedCompany?.id,
        moveTypeId,
        startDate: startDate?.format("YYYY-MM-DD"),
        endDate: endDate?.format("YYYY-MM-DD"),
        ...(purchaseOrderCodeFilter ? { purchaseOrderCode: purchaseOrderCodeFilter } : {}),
      },
      columns,
      addModalData: {
        header: "Nueva entrada sintética",
        body: (
          <SyntheticInboundStockMoveForm
            companyId={selectedCompany?.id}
            moveTypeId={moveTypeId}
            entityPOTypeId={entityPOTypeId}
            warehouseOptions={warehouseOptions}
          />
        ),
      },
      editModalData: {
        header: "Editar entrada sintética",
        body: (
          <SyntheticInboundStockMoveForm
            companyId={selectedCompany?.id}
            moveTypeId={moveTypeId}
            entityPOTypeId={entityPOTypeId}
            warehouseOptions={warehouseOptions}
          />
        ),
      },
      canEditRow: (row) => !row.hasSyntheticOutbound,
      // customButtons: [
      //   {
      //     label: `Generar salidas (${selectedRows.length})`,
      //     icon: "post_add",
      //     color: "secondary",
      //     onClick: handleGenerateOutbounds,
      //     showOnlyWithSelection: true,
      //   },
      // ],
      dataGridProps: {
        getRowId: (row) => String(row.id),
        checkboxSelection: true,
        disableRowSelectionOnClick: true,
        rowSelectionModel: selectedRows,
        onRowSelectionModelChange: (selectionModel) => {
          const ids = Array.isArray(selectionModel)
            ? selectionModel
            : [selectionModel];
          setSelectedRows(ids.filter((item) => typeof item === "number" && item > 0));
        },
        isRowSelectable: (params) => !params.row.hasSyntheticOutbound,
        getRowHeight: () => "auto",
        getEstimatedRowHeight: (params) => {
          const n = (params.model.receiptAcknowledgements || []).length;
          return Math.max(52, 8 + n * 28);
        },
        sx: {
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "flex-start",
            py: 1,
          },
          "& .MuiDataGrid-cellCheckbox": {
            alignItems: "center",
          },
        },
      },
      search: false,
    }),
    [
      columns,
      entityPOTypeId,
      endDate,
      fetchAllPages,
      moveTypeId,
      purchaseOrderCodeFilter,
      selectedCompany?.id,
      selectedRows,
      startDate,
      warehouseOptions,
    ]
  );

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  if (!moveTypeId || !entityPOTypeId) {
    return (
      <Box p={4}>
        <Typography>Cargando configuración de movimientos…</Typography>
      </Box>
    );
  }

  return (
    <Box width="100%" sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <MyDatePicker
              label="Fecha Inicial"
              value={startDate}
              onChange={(newValue) => setStartDate(dayjs(newValue))}
            />
            <MyDatePicker
              label="Fecha Final"
              value={endDate}
              onChange={(newValue) => setEndDate(dayjs(newValue))}
            />
            <TextField
              label="Código OC"
              value={purchaseOrderCodeInput}
              onChange={(e) => setPurchaseOrderCodeInput(e.target.value)}
              size="small"
              sx={{ minWidth: 160 }}
              placeholder="Filtrar…"
            />
          </Stack>
          <Button variant="outlined" onClick={handleExportCsv}>
            Exportar CSV
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ height: 600, width: "100%" }}>
        <LGModalCrud props={crudProps} />
      </Paper>
    </Box>
  );
}
